import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { getSubAppPortalContainer } from "@eweadn-qiankun/shared-utils";
import { Iconfont } from "../Iconfont";
import type { DrawerPlacement, DrawerProps } from "./types";
import "./Drawer.css";

const ANIMATION_MS = 300;

const DEFAULT_WIDTH = 378;
const DEFAULT_HEIGHT = 378;
const LARGE_WIDTH = 736;
const LARGE_HEIGHT = 736;

function resolveOpen(props: Pick<DrawerProps, "open" | "visible">) {
  return props.open ?? props.visible ?? false;
}

function resolveContainer(
  getContainer: DrawerProps["getContainer"]
): HTMLElement | null {
  if (getContainer === false) return null;
  if (typeof getContainer === "function") return getContainer();
  if (getContainer instanceof HTMLElement) return getContainer;
  return getSubAppPortalContainer();
}

function resolvePanelSize(
  placement: DrawerPlacement,
  width: DrawerProps["width"],
  height: DrawerProps["height"],
  size: NonNullable<DrawerProps["size"]>
) {
  const isHorizontal = placement === "left" || placement === "right";
  if (isHorizontal) {
    if (width !== undefined) return { width, height: "100%" as const };
    return {
      width: size === "large" ? LARGE_WIDTH : DEFAULT_WIDTH,
      height: "100%" as const,
    };
  }
  if (height !== undefined) return { width: "100%" as const, height };
  return {
    width: "100%" as const,
    height: size === "large" ? LARGE_HEIGHT : DEFAULT_HEIGHT,
  };
}

export const Drawer = ({
  open,
  visible,
  placement = "right",
  width,
  height,
  size = "default",
  title,
  header,
  extra,
  footer,
  closable = true,
  closeIcon,
  mask = true,
  maskClosable = true,
  getContainer,
  destroyOnClose = false,
  forceRender = false,
  keyboard = true,
  zIndex = 1000,
  onClose,
  afterOpenChange,
  rootClassName,
  className,
  style,
  rootStyle,
  bodyStyle,
  headerStyle,
  footerStyle,
  maskStyle,
  classNames,
  styles,
  children,
}: DrawerProps) => {
  const { t } = useTranslation();
  const isOpen = resolveOpen({ open, visible });
  const [mounted, setMounted] = useState(isOpen);
  const [animating, setAnimating] = useState(false);
  const afterOpenChangeRef = useRef(afterOpenChange);
  afterOpenChangeRef.current = afterOpenChange;

  const embedded = getContainer !== undefined && getContainer !== false;

  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setAnimating(true);
        });
      });
      if (!embedded) {
        document.body.style.overflow = "hidden";
      }
      return;
    }

    if (!mounted) return;

    setAnimating(false);
    const timer = window.setTimeout(() => {
      setMounted(false);
      if (!embedded) {
        document.body.style.overflow = "";
      }
      afterOpenChangeRef.current?.(false);
    }, ANIMATION_MS);

    return () => window.clearTimeout(timer);
  }, [embedded, isOpen, mounted]);

  useEffect(() => {
    if (isOpen && animating) {
      afterOpenChangeRef.current?.(true);
    }
  }, [animating, isOpen]);

  useEffect(() => {
    return () => {
      if (!embedded) {
        document.body.style.overflow = "";
      }
    };
  }, [embedded]);

  const handleClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  useEffect(() => {
    if (!keyboard || !isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      handleClose();
    };

    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [handleClose, isOpen, keyboard]);

  const panelSize = useMemo(
    () => resolvePanelSize(placement, width, height, size),
    [height, placement, size, width]
  );

  const handleMaskClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (maskClosable && event.target === event.currentTarget) {
      handleClose();
    }
  };

  const renderHeader = () => {
    if (header !== undefined) {
      return (
        <div
          className={`shared-drawer-header ${classNames?.header || ""}`.trim()}
          style={{ ...headerStyle, ...styles?.header }}
        >
          {header}
        </div>
      );
    }

    if (!title && !extra && !closable) return null;

    return (
      <div
        className={`shared-drawer-header ${classNames?.header || ""}`.trim()}
        style={{ ...headerStyle, ...styles?.header }}
      >
        <div className="shared-drawer-header-main">
          {title ? <div className="shared-drawer-title">{title}</div> : null}
          {extra ? <div className="shared-drawer-extra">{extra}</div> : null}
        </div>
        {closable ? (
          <button
            type="button"
            className="shared-drawer-close"
            onClick={handleClose}
            aria-label={t("common.close")}
          >
            {closeIcon ?? (
              <Iconfont icon="close" className="shared-drawer-close-icon" />
            )}
          </button>
        ) : null}
      </div>
    );
  };

  if (!mounted && !forceRender) return null;
  if (destroyOnClose && !isOpen && !mounted) return null;

  const drawerContent = (
    <div
      className={[
        "shared-drawer-root",
        embedded ? "is-embedded" : "is-fixed",
        `is-${placement}`,
        animating ? "is-open" : "is-closed",
        rootClassName,
        classNames?.root,
      ]
        .filter(Boolean)
        .join(" ")}
      style={{ zIndex, ...rootStyle, ...styles?.root }}
    >
      {mask ? (
        <div
          className={`shared-drawer-mask ${classNames?.mask || ""}`.trim()}
          style={{ ...maskStyle, ...styles?.mask }}
          onClick={handleMaskClick}
        />
      ) : null}
      <div
        className={["shared-drawer-panel", classNames?.wrapper, className]
          .filter(Boolean)
          .join(" ")}
        style={{
          ...panelSize,
          ...style,
          ...styles?.wrapper,
        }}
      >
        {renderHeader()}
        <div
          className={`shared-drawer-body ${classNames?.body || ""}`.trim()}
          style={{ ...bodyStyle, ...styles?.body }}
        >
          {destroyOnClose && !isOpen && !mounted ? null : children}
        </div>
        {footer !== false && footer !== undefined ? (
          <div
            className={`shared-drawer-footer ${classNames?.footer || ""}`.trim()}
            style={{ ...footerStyle, ...styles?.footer }}
          >
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );

  if (getContainer === false) {
    return drawerContent;
  }

  const container = resolveContainer(getContainer);
  if (!container) return drawerContent;

  return createPortal(drawerContent, container);
};
